import type { FC } from 'react';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { StaticActions as actions } from '@alfa-bank/site-form-utils/actions';

import { checkWarningTextDefault, notFullWarningTextDefault, DEFAULT_MAX_LENGTH } from './config';
import type { TFio, TFormOneFieldFioAnalytics } from './models';
import { warningIcon } from './icon/warning-icon';
import {
    getWarningText,
    onChangeGender,
    onOneFieldFioComplete,
    onChangeNamePart,
    getSettingsMappedByFieldNames,
} from './helpers';

export const OneFieldFio: FC<TFio> = props => {
    const {
        AnalyticsEventSender,
        Input,
        Fields,
        Field,
        FioField,
        OneFieldFioInput,
        RadioTagsController,
        validateNotEmpty,
        checkWarningText = checkWarningTextDefault,
        notFullWarningText = notFullWarningTextDefault,
        name: rawName,
        settings,
        analytics = {} as TFormOneFieldFioAnalytics,
        autoComplete = 'off',
        onSelect,
        genderTagsSize = 's',
        disabledValue,
        disabledName,
        fioCaption,
        fioPlaceholder,
        styles,
        fioWrapperClassNameHelper,
        requestDelay,
        minRequestLength,
        maxLength = DEFAULT_MAX_LENGTH,
        hideStatusIcon,
        hideSuggestions,
        dataTestId,
        expandFields = false,
        checkboxColor = 'red',
    } = props;

    const [isOneField, setIsOneField] = useState(!expandFields);
    const [isGenderShown, setIsGenderShown] = useState(expandFields);

    const name = actions.getNames(rawName);

    const settingsMappedByFieldNames = getSettingsMappedByFieldNames(name, settings);

    const caption = fioCaption || settings.lastName?.caption;
    const placeholder = fioPlaceholder || settings.lastName?.placeholder;

    const { expandToThreeFields, fioValid, ...mainAnalytics } = analytics;

    const fioWrapperClassName = useMemo(
        () => fioWrapperClassNameHelper(),
        [fioWrapperClassNameHelper]
    );

    useLayoutEffect(() => {
        setIsOneField(!expandFields);
    }, [expandFields]);

    if (disabledValue) {
        return (
            <>
                <Input
                    disabled
                    isRequired
                    name={disabledName || 'name'}
                    settings={{
                        caption,
                        placeholder,
                    }}
                    styler={{ marginBottom: 20 }}
                />
                <Fields
                    fields={[name.lastName, name.firstName, name.middleName, name.sex]}
                    render={(lastName, firstName, middleName, gender) => (
                        <>
                            <Field
                                initialValue={lastName.input.value}
                                name="lastName"
                                render={({ input }) => (
                                    <input name={input.name} type="hidden" value={input.value} />
                                )}
                            />
                            <Field
                                initialValue={firstName.input.value}
                                name="firstName"
                                render={({ input }) => (
                                    <input name={input.name} type="hidden" value={input.value} />
                                )}
                            />
                            <Field
                                initialValue={middleName.input.value}
                                name="middleName"
                                render={({ input }) => (
                                    <input name={input.name} type="hidden" value={input.value} />
                                )}
                            />
                            <Field
                                initialValue={gender.input.value}
                                name="gender"
                                render={({ input }) => (
                                    <input name={input.name} type="hidden" value={input.value} />
                                )}
                            />
                        </>
                    )}
                />
            </>
        );
    }

    return (
        <Fields
            data={settingsMappedByFieldNames}
            dispatchesFocus
            fields={[name.lastName, name.firstName, name.middleName, name.sex]}
            receivesFocus
            render={(lastName, firstName, middleName, gender) => (
                <>
                    {isOneField && (
                        <div className={styles.field}>
                            <OneFieldFioInput
                                analytics={mainAnalytics}
                                autoComplete={autoComplete}
                                caption={caption}
                                dataTestId={dataTestId}
                                forwardedRef={actions.getRef(lastName.meta)}
                                hideStatusIcon={hideStatusIcon}
                                hideSuggestions={hideSuggestions}
                                maxLength={maxLength}
                                minRequestLength={minRequestLength}
                                onChangeNamePart={onChangeNamePart({
                                    lastName,
                                    firstName,
                                    middleName,
                                    gender,
                                })}
                                onComplete={onOneFieldFioComplete(
                                    {
                                        lastName,
                                        firstName,
                                        middleName,
                                        gender,
                                    },
                                    setIsGenderShown,
                                    setIsOneField,
                                    {
                                        expandToThreeFields,
                                        fioValid,
                                    }
                                )}
                                onSelect={({ field }) => {
                                    onSelect?.(field);
                                }}
                                placeholder={placeholder}
                                requestDelay={requestDelay}
                                value={[
                                    lastName.input.value,
                                    firstName.input.value,
                                    middleName.input.value,
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                            />
                            <input name="lastName" type="hidden" value={lastName.input.value} />
                            <input name="firstName" type="hidden" value={firstName.input.value} />
                            <input name="middleName" type="hidden" value={middleName.input.value} />
                        </div>
                    )}
                    {!isOneField && (
                        <div className={fioWrapperClassName} data-test-id="three-field-fio">
                            <div className={cn(styles.warningContainer, styles.field)}>
                                <span>{warningIcon}</span>
                                <span data-test-id="fio-data-warning">
                                    {getWarningText(
                                        {
                                            lastName,
                                            firstName,
                                            middleName,
                                            gender,
                                        },
                                        checkWarningText,
                                        notFullWarningText
                                    )}{' '}
                                </span>
                            </div>
                            <div>
                                <FioField
                                    analytics={mainAnalytics}
                                    firstName={firstName}
                                    firstNameRef={actions.getRef(firstName.meta)}
                                    hasMiddleNameSettings={{ color: checkboxColor }}
                                    hideStatusIcon={hideStatusIcon}
                                    lastName={lastName}
                                    lastNameRef={actions.getRef(lastName.meta)}
                                    maxLength={maxLength}
                                    middleName={middleName}
                                    middleNameRef={actions.getRef(middleName.meta)}
                                    onBlur={({ field: { input }, value }) => {
                                        actions.onChange(input)(value);
                                        actions.onBlur(input)();
                                    }}
                                    onCancel={field => actions.onCancel(field)}
                                    onChange={({ field: { input }, value }) =>
                                        actions.onChange(input)(value)
                                    }
                                    onFocus={({ field: { input } }) => actions.onFocus(input)()}
                                    onSelect={({ field, value }) => {
                                        onSelect?.(field);
                                        actions.onSelect(field)(value);
                                    }}
                                    requestDelay={requestDelay}
                                    styler={{ marginBottom: 20 }}
                                    withGender={false}
                                />
                            </div>
                        </div>
                    )}
                    {isGenderShown && (
                        <div className={styles.field} data-test-id="gender-tags">
                            {mainAnalytics.gender ? (
                                <AnalyticsEventSender
                                    $events={{
                                        onChange: {
                                            data: mainAnalytics.gender,
                                            switchData: {
                                                '$1.value': {
                                                    m: {
                                                        label_detail: `${mainAnalytics.gender.label_detail} - мужской`,
                                                    },
                                                    f: {
                                                        label_detail: `${mainAnalytics.gender.label_detail} - женский`,
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                >
                                    <RadioTagsController
                                        genderSettings={settings.sex}
                                        name={name.sex}
                                        onChange={onChangeGender(gender)}
                                        size={genderTagsSize}
                                    />
                                </AnalyticsEventSender>
                            ) : (
                                <RadioTagsController
                                    genderSettings={settings.sex}
                                    name={name.sex}
                                    onChange={onChangeGender(gender)}
                                    size={genderTagsSize}
                                />
                            )}
                        </div>
                    )}
                    <input name="gender" type="hidden" value={gender.input.value} />
                </>
            )}
            validate={validateNotEmpty()}
        />
    );
};
